import React, { useRef, useEffect } from "react";
import { Animated, Platform, Pressable, StyleSheet, View } from "react-native";
import { StyleFunction, useTheme, useThemedStyle } from "../../theme";
import Clipboard from "@react-native-clipboard/clipboard";
import { Color } from "react-color";
import { useToast } from "../toast";
import { t } from "i18next";

export type ColorIndicatorStatus = "success" | "warning" | "error" | "inactive";

export function ColorIndicator({
    status,
    color,
    size,
    animate = true,
    copyOnClick = false,
}: {
    status?: ColorIndicatorStatus;
    color?: string;
    size?: "small" | "normal" | "large";
    animate?: boolean;
    copyOnClick?: boolean;
}) {
    const styles = useThemedStyle(styleFunc);
    const theme = useTheme();
    const toast = useToast();

    const fadeAnim = useRef(new Animated.Value(0)).current;

    const fadeIn = () => {
        Animated.timing(fadeAnim, {
            useNativeDriver: Platform.OS !== "web",
            toValue: 1,
            duration: 600,
        }).start();
    };

    let indicatorColor: Extract<Color, string>;

    if (!color) {
        switch (status) {
            case "success": {
                indicatorColor = theme.colors.success;

                break;
            }

            case "warning": {
                indicatorColor = theme.colors.warning;

                break;
            }

            case "error": {
                indicatorColor = theme.colors.error;

                break;
            }
            case "inactive": {
                indicatorColor = theme.colors.grey500;

                break;
            }
            default: {
                indicatorColor = theme.colors.white;
                break;
            }
        }
    } else {
        indicatorColor = color;
    }

    let sizeStyles;
    switch (size) {
        case "small": {
            sizeStyles = styles.smallIndicator;
            break;
        }
        case "large": {
            sizeStyles = styles.largeIndicator;
            break;
        }
        case "normal":
        default: {
            sizeStyles = styles.normalIndicator;
        }
    }

    const flattenedStyles = StyleSheet.flatten([
        styles.baseIndicator,
        sizeStyles,
        { backgroundColor: indicatorColor },
    ]);

    const buttonClick = () => {
        if (!copyOnClick) {
            return;
        }

        Clipboard.setString(indicatorColor);
        toast.info(
            t(
                "backoffice.info.color_copied",

                "Color code {{ colorCode }} copied",
                { replace: { colorCode: indicatorColor } }
            )
        );
    };

    useEffect(fadeIn);

    if (!animate) {
        return (
            <Pressable onPress={buttonClick}>
                <View style={flattenedStyles} />
            </Pressable>
        );
    }

    return (
        <Pressable onPress={buttonClick}>
            <Animated.View
                style={[
                    flattenedStyles,
                    {
                        opacity: fadeAnim,
                    },
                ]}
            />
        </Pressable>
    );
}

const styleFunc: StyleFunction = theme => ({
    baseIndicator: {
        borderColor: theme.colors.semiTransparentBlack,
        borderWidth: StyleSheet.hairlineWidth,
    },

    normalIndicator: {
        borderRadius: theme.spacingScale / 2,
        width: theme.spacingScale,
        height: theme.spacingScale,
    },

    smallIndicator: {
        borderRadius: theme.spacingScale / 4,
        width: theme.spacingScale / 2,
        height: theme.spacingScale / 2,
    },

    largeIndicator: {
        borderRadius: theme.spacingScale,
        width: theme.spacingScale * 2,
        height: theme.spacingScale * 2,
    },
});
